import { useEffect } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  CrossDayAnalogueNarrative,
  CrossDayNarrativeDayEntry,
  CrossDayNarrativeSecondary,
  CrossDayWindowSession,
} from '@/services/api';

function formatPct(value: number) {
  return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
}

function formatDateLabel(value: string): string {
  try {
    const date = new Date(`${value}T12:00:00`);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return value;
  }
}

function formatWindowLabel(start: string, end: string): string {
  try {
    const s = new Date(`${start}T12:00:00`);
    const e = new Date(`${end}T12:00:00`);
    const opts: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
    return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', opts)}`;
  } catch {
    return `${start} – ${end}`;
  }
}

function moveBarColor(pct: number) {
  return pct >= 0 ? '#22c55e' : '#ef4444';
}

function useRelayoutChartsOnPrint() {
  useEffect(() => {
    const relayout = () => window.dispatchEvent(new Event('resize'));
    window.addEventListener('beforeprint', relayout);
    window.addEventListener('afterprint', relayout);
    return () => {
      window.removeEventListener('beforeprint', relayout);
      window.removeEventListener('afterprint', relayout);
    };
  }, []);
}

function handleDownloadPdf() {
  window.dispatchEvent(new Event('resize'));
  requestAnimationFrame(() => window.print());
}

function Card({ className = '', children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={`rounded-xl border border-border bg-card print:border-0 print:bg-muted/30 print:shadow-none ${className}`}
    >
      {children}
    </div>
  );
}

function Badge({
  variant = 'outline',
  className = '',
  children,
}: {
  variant?: 'outline' | 'secondary' | 'primary';
  className?: string;
  children: React.ReactNode;
}) {
  const base = 'inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium';
  const variants = {
    outline: 'border border-border',
    secondary: 'bg-secondary text-secondary-foreground',
    primary: 'bg-primary text-primary-foreground',
  };
  return <span className={`${base} ${variants[variant]} ${className}`}>{children}</span>;
}

function Separator({ className = '' }: { className?: string }) {
  return <hr className={`border-0 border-t border-border print:border-gray-200 ${className}`} />;
}

function confidenceClass(confidence: string | undefined) {
  const c = (confidence ?? '').toLowerCase();
  if (c === 'high') return 'border-emerald-200 bg-emerald-500/10 text-emerald-700';
  if (c.includes('medium')) return 'border-amber-200 bg-amber-500/10 text-amber-700';
  return 'border-border bg-muted/50 text-muted-foreground';
}

function ProseBlock({ text, className = '' }: { text: string; className?: string }) {
  const paragraphs = text.split(/\n\n+/).filter(Boolean);
  return (
    <div className={`space-y-4 ${className}`}>
      {paragraphs.map((paragraph, index) => (
        <p key={index} className="leading-relaxed text-foreground">
          {paragraph}
        </p>
      ))}
    </div>
  );
}

function secondaryCurrentState(narrative: CrossDayNarrativeSecondary): string {
  if (typeof narrative.current_state === 'string') {
    return narrative.current_state;
  }
  return narrative.current_state?.summary ?? '';
}

function DayTimeline({
  entries,
  showEngineState = false,
}: {
  entries: CrossDayNarrativeDayEntry[];
  showEngineState?: boolean;
}) {
  return (
    <div className="space-y-4">
      {entries.map((entry, index) => (
        <div key={entry.date} className="flex gap-4 print:break-inside-avoid">
          <div className="flex w-24 shrink-0 flex-col items-end pt-0.5">
            <span className="text-xs font-semibold text-foreground">{formatDateLabel(entry.date)}</span>
            {showEngineState && entry.engine_state ? (
              <span className="mt-1 text-right text-[10px] leading-tight text-muted-foreground">
                {entry.engine_state}
              </span>
            ) : null}
          </div>
          <div className="relative min-w-0 flex-1 pb-1">
            {index < entries.length - 1 ? (
              <span className="absolute bottom-0 left-[5px] top-3 w-px bg-border" aria-hidden />
            ) : null}
            <span
              className="absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full border-2 border-amber-400 bg-background"
              aria-hidden
            />
            <p className="pl-5 text-sm leading-relaxed text-foreground">{entry.read}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function SessionTapeTable({ sessions }: { sessions: CrossDayWindowSession[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="pb-2 pr-3 font-medium">Date</th>
            <th className="pb-2 pr-3 font-medium">SPY</th>
            <th className="pb-2 pr-3 font-medium">QQQ</th>
            <th className="pb-2 pr-3 font-medium">XLK</th>
            <th className="pb-2 pr-3 font-medium">10Y</th>
            <th className="pb-2 pr-3 font-medium">VIX</th>
            <th className="pb-2 pr-3 font-medium">Breadth</th>
            <th className="pb-2 font-medium">Leaders / laggards</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {sessions.map((row) => (
            <tr key={row.date} className="transition-colors hover:bg-muted/30">
              <td className="py-2.5 pr-3 align-top">
                <div className="font-medium">{formatDateLabel(row.date)}</div>
                <div className="text-xs text-muted-foreground">{row.label}</div>
              </td>
              <td
                className="py-2.5 pr-3 font-medium tabular-nums"
                style={{ color: moveBarColor(row.spy_pct) }}
              >
                {formatPct(row.spy_pct)}
              </td>
              <td
                className="py-2.5 pr-3 font-medium tabular-nums"
                style={{ color: moveBarColor(row.qqq_pct) }}
              >
                {formatPct(row.qqq_pct)}
              </td>
              <td
                className="py-2.5 pr-3 font-semibold tabular-nums"
                style={{ color: moveBarColor(row.xlk_pct) }}
              >
                {formatPct(row.xlk_pct)}
              </td>
              <td className="py-2.5 pr-3 tabular-nums text-muted-foreground">{row.ten_year_yield_pct}%</td>
              <td className="py-2.5 pr-3 tabular-nums text-muted-foreground">{row.vix}</td>
              <td className="py-2.5 pr-3 text-muted-foreground">
                {row.sectors_up}↑ / {row.sectors_down}↓
              </td>
              <td className="py-2.5 text-xs leading-relaxed text-muted-foreground">
                <span className="text-emerald-700">{row.leaders}</span>
                <span className="mx-1">·</span>
                <span className="text-red-700">{row.laggards}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SecondaryNarrativeSection({ narrative }: { narrative: CrossDayNarrativeSecondary }) {
  return (
    <div className="mb-10 print:break-inside-avoid">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="text-xl font-semibold">{narrative.title}</h3>
        {narrative.confidence ? (
          <Badge variant="outline" className={confidenceClass(narrative.confidence)}>
            {narrative.confidence} confidence
          </Badge>
        ) : null}
        {narrative.trend_direction ? (
          <Badge variant="secondary">{narrative.trend_direction}</Badge>
        ) : null}
      </div>
      {narrative.thesis ? (
        <p className="mb-5 leading-relaxed text-muted-foreground">{narrative.thesis}</p>
      ) : null}
      {narrative.day_by_day?.length ? (
        <Card className="mb-5 bg-muted/40 p-5">
          <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Tracked across the window
          </p>
          <DayTimeline entries={narrative.day_by_day} />
        </Card>
      ) : null}
      {secondaryCurrentState(narrative) || narrative.current_state ? (
        <div className="border-l-2 border-amber-300 pl-4">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Current state
            {typeof narrative.current_state === 'object' && narrative.current_state?.as_of
              ? ` · ${narrative.current_state.as_of}`
              : ''}
          </p>
          <p className="leading-relaxed">{secondaryCurrentState(narrative)}</p>
          {typeof narrative.current_state === 'object' && narrative.current_state?.implication ? (
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              <strong className="text-foreground">Implication:</strong>{' '}
              {narrative.current_state.implication}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ProseCrossDayReport({ narrative }: { narrative: CrossDayAnalogueNarrative }) {
  const { metadata, primary_narrative, secondary_narratives, synthesis, situations_to_monitor, dangerous_upcoming_window } =
    narrative;

  return (
    <article className="cross-day-analogue-report mx-auto max-w-3xl px-6 py-12 print:max-w-none print:px-0 print:py-0">
      <div className="mb-6 flex justify-end print:hidden">
        <button
          type="button"
          onClick={handleDownloadPdf}
          className="flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm transition-colors hover:bg-muted"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 6 2 18 2 18 9" />
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
            <rect x="6" y="14" width="12" height="8" />
          </svg>
          Download as PDF
        </button>
      </div>

      <header className="mb-10">
        <time className="mb-2 block text-sm text-muted-foreground">
          {formatWindowLabel(metadata.window_start, metadata.window_end)} ·{' '}
          {metadata.sessions_covered} sessions
        </time>
        <h1 className="text-4xl font-bold leading-tight">Cross-day narrative synthesis</h1>
      </header>

      <section className="mb-12">
        <h2 className="mb-5 text-2xl font-bold">Primary narrative</h2>
        <h3 className="mb-5 text-xl font-semibold leading-snug text-foreground">
          {primary_narrative.title}
        </h3>
        {primary_narrative.body ? (
          <ProseBlock text={primary_narrative.body} />
        ) : primary_narrative.thesis ? (
          <ProseBlock text={primary_narrative.thesis} />
        ) : null}
      </section>

      <Separator className="my-10" />

      <section className="mb-12">
        <h2 className="mb-8 text-2xl font-bold">Secondary narratives</h2>
        <div className="space-y-12">
          {secondary_narratives.map((sec) => (
            <div key={sec.id} className="print:break-inside-avoid">
              <h3 className="mb-4 text-xl font-semibold leading-snug">{sec.title}</h3>
              {sec.arc ? <ProseBlock text={sec.arc} className="mb-5 text-muted-foreground" /> : null}
              {secondaryCurrentState(sec) ? (
                <div className="border-l-2 border-amber-300 pl-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Where it stands now
                  </p>
                  <p className="leading-relaxed">{secondaryCurrentState(sec)}</p>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </section>

      <Separator className="my-10" />

      <section className="mb-12">
        <h2 className="mb-5 text-2xl font-bold">Current synthesis</h2>
        {synthesis.body ? (
          <ProseBlock text={synthesis.body} />
        ) : synthesis.the_one_thesis ? (
          <ProseBlock text={synthesis.the_one_thesis} />
        ) : null}
      </section>

      {situations_to_monitor?.length ? (
        <>
          <Separator className="my-10" />
          <section className="mb-12">
            <h2 className="mb-6 text-2xl font-bold">Three situations to risk-monitor</h2>
            <div className="space-y-8">
              {situations_to_monitor.map((situation) => (
                <div key={situation.rank} className="print:break-inside-avoid">
                  <h3 className="mb-3 text-lg font-semibold">
                    {situation.rank}. {situation.title}
                  </h3>
                  <ProseBlock text={situation.body} className="text-muted-foreground" />
                </div>
              ))}
            </div>
          </section>
        </>
      ) : null}

      {dangerous_upcoming_window ? (
        <>
          <Separator className="my-10" />
          <section className="mb-12">
            <h2 className="mb-5 text-2xl font-bold">Most dangerous upcoming window</h2>
            <Card className="border-red-200 bg-red-50/40 p-6 print:break-inside-avoid">
              <h3 className="mb-4 text-lg font-semibold text-red-900">
                {dangerous_upcoming_window.title}
              </h3>
              <ProseBlock text={dangerous_upcoming_window.body} />
            </Card>
          </section>
        </>
      ) : null}
    </article>
  );
}

function DetailedCrossDayReport({ narrative }: { narrative: CrossDayAnalogueNarrative }) {
  const { metadata, primary_narrative, secondary_narratives, synthesis, window_tape_reference } =
    narrative;
  const sessions = window_tape_reference?.sessions ?? [];
  const xlkSeq = window_tape_reference?.xlk_whipsaw_sequence;
  const xlkChartData =
    xlkSeq?.dates.map((date, i) => ({
      date: formatDateLabel(date).replace(',', ''),
      xlk: xlkSeq.xlk_pct[i] ?? 0,
    })) ?? [];

  return (
    <article className="cross-day-analogue-report mx-auto max-w-3xl px-6 py-12 print:max-w-none print:px-0 print:py-0">
      <div className="mb-6 flex justify-end print:hidden">
        <button
          type="button"
          onClick={handleDownloadPdf}
          className="flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm transition-colors hover:bg-muted"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 6 2 18 2 18 9" />
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
            <rect x="6" y="14" width="12" height="8" />
          </svg>
          Download as PDF
        </button>
      </div>

      <header className="mb-8">
        <time className="mb-2 block text-sm text-muted-foreground">
          {formatWindowLabel(metadata.window_start, metadata.window_end)} ·{' '}
          {metadata.sessions_covered} sessions
        </time>
        <h1 className="mb-3 text-4xl font-bold">Cross-day analogue analysis</h1>
        {primary_narrative.headline ? (
          <p className="text-lg leading-relaxed text-muted-foreground">{primary_narrative.headline}</p>
        ) : null}
      </header>

      {synthesis.the_one_thesis ? (
        <Card className="mb-8 border-amber-200 bg-amber-50/50 p-6 print:break-inside-avoid">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-amber-800">
            The one thesis
          </p>
          <p className="text-lg leading-relaxed">{synthesis.the_one_thesis}</p>
        </Card>
      ) : null}

      {sessions.length > 0 ? (
        <>
          <section className="mb-12">
            <h2 className="mb-2 text-2xl font-bold">Window tape</h2>
            <p className="mb-5 text-muted-foreground">
              {window_tape_reference?.description ??
                'Shared fact table — every narrative below references the same numbers.'}
            </p>
            <Card className="mb-6 bg-muted/40 p-5">
              <SessionTapeTable sessions={sessions} />
            </Card>

            {xlkChartData.length > 0 ? (
              <Card className="bg-muted/40 p-5">
                <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  XLK whipsaw — the single engine oscillating
                </p>
                <div style={{ width: '100%', height: 220, minHeight: 220 }}>
                  <ResponsiveContainer width="100%" height="100%" debounce={1}>
                    <BarChart data={xlkChartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis
                        tickFormatter={(v) => `${v > 0 ? '+' : ''}${v}%`}
                        tick={{ fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <ReferenceLine y={0} stroke="rgba(0,0,0,0.15)" />
                      <Tooltip
                        formatter={(value: number) => [formatPct(value), 'XLK']}
                        contentStyle={{ fontSize: 12 }}
                      />
                      <Bar dataKey="xlk" name="XLK" radius={[3, 3, 0, 0]} maxBarSize={40}>
                        {xlkChartData.map((entry) => (
                          <Cell key={entry.date} fill={moveBarColor(entry.xlk)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {xlkSeq?.interpretation ? (
                  <p className="mt-3 text-center text-xs leading-relaxed text-muted-foreground">
                    {xlkSeq.interpretation}
                  </p>
                ) : null}
              </Card>
            ) : null}
          </section>

          <Separator className="my-8" />
        </>
      ) : null}

      <section className="mb-12">
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <h2 className="text-2xl font-bold">Primary narrative</h2>
          <Badge variant="primary">Spine</Badge>
          {primary_narrative.confidence ? (
            <Badge variant="outline" className={confidenceClass(primary_narrative.confidence)}>
              {primary_narrative.confidence} confidence
            </Badge>
          ) : null}
        </div>
        <h3 className="mb-3 text-xl font-semibold">{primary_narrative.title}</h3>
        {primary_narrative.thesis ? (
          <p className="mb-5 text-lg leading-relaxed">{primary_narrative.thesis}</p>
        ) : null}
        {primary_narrative.risk_skew ? (
          <p className="mb-5 text-sm text-muted-foreground">
            <strong className="text-foreground">Risk skew:</strong> {primary_narrative.risk_skew}
          </p>
        ) : null}
        {primary_narrative.day_by_day?.length ? (
          <Card className="mb-5 bg-muted/40 p-5">
            <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Day-by-day engine state
            </p>
            <DayTimeline entries={primary_narrative.day_by_day} showEngineState />
          </Card>
        ) : null}
        {primary_narrative.current_state ? (
          <div className="border-l-2 border-emerald-300 pl-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Current state · {primary_narrative.current_state.as_of ?? metadata.reference_date}
              {primary_narrative.current_state.trend
                ? ` · ${primary_narrative.current_state.trend}`
                : ''}
            </p>
            <p className="leading-relaxed">{primary_narrative.current_state.summary}</p>
            {primary_narrative.current_state.primary_tell_to_watch ? (
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                <strong className="text-foreground">Tell to watch:</strong>{' '}
                {primary_narrative.current_state.primary_tell_to_watch}
              </p>
            ) : null}
          </div>
        ) : null}
      </section>

      <Separator className="my-8" />

      <section className="mb-12">
        <h2 className="mb-2 text-2xl font-bold">Secondary narratives</h2>
        <p className="mb-8 text-muted-foreground">
          Distinct threads evolving on their own clock underneath the primary spine — each tracked
          session-by-session.
        </p>
        {secondary_narratives.map((sec) => (
          <SecondaryNarrativeSection key={sec.id} narrative={sec} />
        ))}
      </section>

      <Separator className="my-8" />

      <section className="mb-12">
        <h2 className="mb-6 text-2xl font-bold">Synthesis</h2>
        <p className="mb-6 leading-relaxed text-muted-foreground">
          {synthesis.description ??
            'Combined read of the primary and secondary narratives as of the reference date.'}
        </p>

        {synthesis.secondary_narratives_state?.length ? (
          <div className="mb-8 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">Thread</th>
                  <th className="pb-2 pr-4 font-medium">Current state</th>
                  <th className="pb-2 font-medium">Change</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr className="bg-muted/20">
                  <td className="py-2.5 pr-4 font-semibold">Primary · single engine</td>
                  <td className="py-2.5 pr-4 text-muted-foreground">
                    {synthesis.primary_narrative_state?.state}
                  </td>
                  <td className="py-2.5 text-muted-foreground">
                    {synthesis.primary_narrative_state?.direction}
                  </td>
                </tr>
                {synthesis.secondary_narratives_state.map((row) => (
                  <tr key={row.narrative} className="transition-colors hover:bg-muted/30">
                    <td className="py-2.5 pr-4 font-medium">{row.narrative.replace(/_/g, ' ')}</td>
                    <td className="py-2.5 pr-4 leading-relaxed text-muted-foreground">{row.state}</td>
                    <td className="py-2.5 text-xs text-muted-foreground">{row.change}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {synthesis.convergent_forward_shape ? (
          <Card className="mb-8 bg-muted/50 p-6">
            <h3 className="mb-4 font-semibold">Convergent forward shape</h3>
            <div className="space-y-4 text-sm leading-relaxed">
              {synthesis.convergent_forward_shape.near_term_1_2_weeks ? (
                <p>
                  <strong className="text-foreground">Near term (1–2 weeks):</strong>{' '}
                  {synthesis.convergent_forward_shape.near_term_1_2_weeks}
                </p>
              ) : null}
              {synthesis.convergent_forward_shape.reversal_risk_10_sessions ? (
                <p>
                  <strong className="text-foreground">10-session reversal risk:</strong>{' '}
                  {synthesis.convergent_forward_shape.reversal_risk_10_sessions}
                </p>
              ) : null}
              {synthesis.convergent_forward_shape.drawdown_risk_63_sessions ? (
                <p>
                  <strong className="text-foreground">63-session drawdown risk:</strong>{' '}
                  {synthesis.convergent_forward_shape.drawdown_risk_63_sessions}
                </p>
              ) : null}
              {synthesis.convergent_forward_shape.the_fork ? (
                <p className="border-l-2 border-amber-300 pl-4">
                  <strong className="text-foreground">The fork:</strong>{' '}
                  {synthesis.convergent_forward_shape.the_fork}
                </p>
              ) : null}
            </div>
          </Card>
        ) : null}

        <div className="space-y-5">
          {synthesis.key_branching_point ? (
            <div className="flex gap-3">
              <span className="mt-0.5 shrink-0 font-bold text-amber-500">●</span>
              <p>
                <strong>Key branching point:</strong> {synthesis.key_branching_point}
              </p>
            </div>
          ) : null}
          {synthesis.primary_risk ? (
            <div className="flex gap-3">
              <span className="mt-0.5 shrink-0 font-bold text-red-500">●</span>
              <p>
                <strong>Primary risk:</strong> {synthesis.primary_risk}
              </p>
            </div>
          ) : null}
          {synthesis.primary_opportunity ? (
            <div className="flex gap-3">
              <span className="mt-0.5 shrink-0 font-bold text-emerald-600">●</span>
              <p>
                <strong>Primary opportunity:</strong> {synthesis.primary_opportunity}
              </p>
            </div>
          ) : null}
          {synthesis.primary_tell_to_watch ? (
            <div className="flex gap-3">
              <span className="mt-0.5 shrink-0 font-bold text-amber-500">●</span>
              <p>
                <strong>Tell to watch:</strong> {synthesis.primary_tell_to_watch}
              </p>
            </div>
          ) : null}
        </div>
      </section>
    </article>
  );
}

export function CrossDayAnalogueAnalysisReport({
  narrative,
}: {
  narrative: CrossDayAnalogueNarrative;
}) {
  useRelayoutChartsOnPrint();

  const isProseFormat =
    (narrative.metadata.schema_version ?? 0) >= 4 || Boolean(narrative.primary_narrative.body);

  if (isProseFormat) {
    return <ProseCrossDayReport narrative={narrative} />;
  }

  return <DetailedCrossDayReport narrative={narrative} />;
}
